import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { EcosystemMenu } from "@/components/EcosystemMenu";
import { ReleasePopup } from "@/components/ReleasePopup";
import { ProgereVersionBadge } from "@/components/ProgereVersionBadge";
import { ProgereBrand } from "@/components/ProgereBrand";
import { ProgereDownloadAppButton } from "@/components/shared/ProgereDownloadAppButton";
import { SupportAccessBanner } from "@/components/SupportAccessBanner";
import {
  LayoutDashboard, Grid3x3, Activity, Users, Settings, Bell, LogOut, Menu, X, ChevronDown, LifeBuoy, ShieldCheck, Receipt, Megaphone, Sun, Moon, Rocket, Crown, Wrench, Cloud, FolderOpen,
} from "lucide-react";

const NAV_GROUPS = [
  {
    title: "Pilotage", titleEn: "Overview",
    items: [
      { to: "/app", key: "dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
      { to: "/app/modules", key: "modules", icon: Grid3x3, id: "modules" },
      { to: "/app/activity", key: "activity", icon: Activity, id: "activity" },
      { to: "/app/notifications", key: "notifications", icon: Bell, id: "notifications" },
    ],
  },
  {
    title: "Équipe & compte", titleEn: "Team & billing",
    items: [
      { to: "/app/team", key: "team", icon: Users, id: "team" },
      { to: "/app/billing", key: "billing", icon: Receipt, id: "billing" },
      { to: "/app/cloud-connect", key: "cloudconnect", icon: Cloud, id: "cloudconnect", label: "PROGÈRE Cloud Connect", labelEn: "PROGÈRE Cloud Connect" },
      { to: "/app/documents", key: "documents", icon: FolderOpen, id: "documents", label: "Documents cloud", labelEn: "Cloud documents" },
      { to: "/app/settings", key: "settings", icon: Settings, id: "settings" },
    ],
  },
  {
    title: "Support & outils", titleEn: "Support & tools",
    items: [
      { to: "/app/support", key: "support", icon: LifeBuoy, id: "support" },
      { to: "/app/media-kit", key: "mediakit", icon: Megaphone, id: "mediakit" },
      { to: "/app/versions", key: "versions", icon: Rocket, id: "versions", label: "Versions", labelEn: "Releases", staff: true },
    ],
  },
  {
    title: "Interne PROGÈRE", titleEn: "PROGÈRE Internal",
    items: [
      { to: "/app/centre-createur", key: "creator", icon: Crown, id: "creator", label: "Centre créateur", labelEn: "Creator Center", owner: true },
      { to: "/app/centre-technicien", key: "technician", icon: Wrench, id: "technician", label: "Centre technicien", labelEn: "Technician Center", technician: true },
      { to: "/app/master/cloud-connect", key: "msactivations", icon: Cloud, id: "msactivations", label: "Activations Microsoft", labelEn: "Microsoft Activations", technician: true },
    ],
  },
];

function SidebarContent({ onNavigate }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const activeLabel = lang === "en" ? "Active plan" : "Forfait actif";
  const navLabel = lang === "en" ? "Navigation" : "Navigation";
  const planName = user ? t(`dash.plan.${user.plan}`) : "";

  return (
    <div className="flex h-full flex-col">
      {/* Marque : tuile logo dorée arrondie + mot-symbole */}
      <div className="flex items-center gap-3 px-5 h-[76px] border-b border-white/10">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[#EAB308]/45 bg-gradient-to-br from-[#EAB308]/25 to-[#EAB308]/[0.04] shadow-[0_0_22px_rgba(234,179,8,0.18)]">
          <img src="/favicon.png" alt="PROGÈRE" className="h-8 w-8 rounded-lg object-contain" />
        </div>
        <NavLink to="/app" onClick={onNavigate} className="leading-tight" data-testid="sidebar-brand">
          <ProgereBrand module="CORE" size="medium" />
          <div className="progere-brand-subtitle">
            {lang === "en" ? "PROGÈRE ecosystem module" : "Module de l'écosystème PROGÈRE"}
          </div>
        </NavLink>
      </div>

      {/* Encadré module actif */}
      <div className="px-3 pt-4">
        <div className="flex items-center gap-3 rounded-xl border border-[#EAB308]/35 bg-gradient-to-br from-[#EAB308]/12 to-transparent px-4 py-3" data-testid="sidebar-active-plan">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#EAB308]/12 border border-[#EAB308]/30">
            <ShieldCheck className="w-5 h-5 text-[#EAB308]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-white/45">{lang === "en" ? "Active module" : "Module actif"}</div>
            <ProgereBrand module="CORE" size="small" />
          </div>
        </div>
      </div>

      {user?.is_platform_owner && (
        <div className="px-3 pt-3">
          <div className="flex items-center gap-2 rounded-lg border border-[#EAB308]/40 bg-[#EAB308]/10 px-3 py-1.5" data-testid="owner-badge">
            <Crown className="w-4 h-4 text-[#EAB308]" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#EAB308]">Propriétaire</span>
          </div>
        </div>
      )}

      <nav className="flex-1 px-3 pt-4 pb-6 overflow-y-auto">
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((n) =>
            (n.id !== "mediakit" || user?.is_staff) &&
            (!n.staff || user?.is_staff) &&
            (!n.owner || user?.is_platform_owner) &&
            (!n.technician || user?.is_platform_technician));
          if (items.length === 0) return null;
          return (
            <div key={group.title} className="mb-5">
              <div className="px-2.5 pb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-white/30">
                {lang === "en" ? group.titleEn : group.title}
              </div>
              <div className="space-y-1">
                {items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={onNavigate}
                    data-testid={`sidebar-${n.id}`}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-lg px-3.5 py-2.5 text-sm font-medium transition-colors ${
                        isActive
                          ? "text-white bg-gradient-to-r from-[#dc143c] to-[#dc143c]/45 shadow-[inset_3px_0_0_#ffd45c,0_6px_18px_rgba(220,20,60,0.25)]"
                          : "text-white/70 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <n.icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? "text-white" : "text-[#EAB308]"}`} />
                        {n.label ? (lang === "en" ? n.labelEn : n.label) : t(`dash.nav.${n.key}`)}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="px-3 pb-3">
        <ProgereDownloadAppButton variant="sidebar" size="sm" moduleCode="core" />
      </div>
      <ProgereVersionBadge moduleName="CORE" onClick={onNavigate} />
    </div>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useI18n();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  if (!user) return null;

  const initials = user.name?.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen progere-bg flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/10 bg-[#0a0a0a] fixed inset-y-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 bg-[#0a0a0a] border-r border-white/10">
            <button className="absolute top-5 right-4 text-white/60" onClick={() => setMobileOpen(false)}>
              <X className="w-5 h-5" />
            </button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-[70px] shrink-0 border-b border-white/10 bg-black/60 backdrop-blur-xl flex items-center justify-between px-5 lg:px-8 sticky top-0 z-40">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-white p-1" onClick={() => setMobileOpen(true)} data-testid="mobile-sidebar-toggle">
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <div className="text-sm text-white/50">{user.company_name}</div>
              <div className="text-xs font-mono text-[#EAB308]">{t(`dash.plan.${user.plan}`)}</div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <EcosystemMenu />
            <button
              onClick={toggleTheme}
              data-testid="dashboard-theme-toggle"
              aria-label={theme === "light" ? "Activer le mode sombre" : "Activer le mode clair"}
              title={theme === "light" ? "Mode sombre" : "Mode clair"}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-[#EAB308]/40 transition-colors"
            >
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              aria-label="Language"
              className="flex h-10 items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-3 text-xs font-bold text-white/70 hover:text-white hover:border-[#EAB308]/40 transition-colors"
            >
              <span className={lang === "en" ? "text-[#EAB308]" : ""}>EN</span>
              <span className="text-white/30">/</span>
              <span className={lang === "fr" ? "text-[#EAB308]" : ""}>FR</span>
            </button>
            <button
              onClick={() => navigate("/app/notifications")}
              className="relative flex h-10 w-10 items-center justify-center rounded-md border border-white/10 bg-white/5 text-white/70 hover:text-white hover:border-[#EAB308]/40 transition-colors"
              data-testid="topbar-notifications"
            >
              <Bell className="w-5 h-5" />
            </button>

            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="user-menu-toggle"
                className="flex items-center gap-2.5 rounded-md border border-white/10 bg-white/5 pl-1.5 pr-3 py-1.5 hover:border-white/25 transition-colors"
              >
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-md text-xs font-bold text-black"
                  style={{ background: user.avatar_color || "#EAB308" }}
                >
                  {initials}
                </span>
                <span className="hidden sm:block text-sm text-white/80 max-w-[120px] truncate">{user.name}</span>
                <ChevronDown className="w-4 h-4 text-white/40" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-52 rounded-lg border border-white/10 bg-[#0a0a0a] p-1.5 shadow-xl z-50" onMouseLeave={() => setMenuOpen(false)}>
                  <div className="px-3 py-2 border-b border-white/10 mb-1">
                    <div className="text-sm text-white font-medium truncate">{user.name}</div>
                    <div className="text-xs text-white/40 truncate">{user.email}</div>
                  </div>
                  <button onClick={() => { setMenuOpen(false); navigate("/app/settings"); }} className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white transition-colors">
                    <Settings className="w-4 h-4" /> {t("dash.settings")}
                  </button>
                  <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 rounded-md px-3 py-2 text-sm text-red-300 hover:bg-[#dc143c]/10 transition-colors">
                    <LogOut className="w-4 h-4" /> {t("dash.logout")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 p-5 lg:p-8">
          <SupportAccessBanner />
          <Outlet />
        </main>
      </div>
      <ReleasePopup />
    </div>
  );
}
